import { NAV, SITE, type NavItem } from '../lib/nav';

export function renderContactBlock(variant: 'sidebar' | 'doc' = 'doc'): string {
  const className =
    variant === 'sidebar' ? 'contact-block contact-block--sidebar' : 'contact-block';
  return `
    <div class="${className}">
      <p class="contact-block__label">${SITE.contactLabel}</p>
      <a class="contact-block__email" href="mailto:${SITE.contactEmail}">${SITE.contactEmail}</a>
    </div>
  `;
}

export function renderDocFooter(): string {
  return `
    <footer class="doc-footer">
      ${renderContactBlock('doc')}
      <p class="doc-footer__legal">Legal disclaimer: this wiki describes product intent and shipped capabilities. It is not legal advice. Consult counsel for compliance strategy.</p>
    </footer>
  `;
}

export function renderShell(): string {
  return `
    <div class="shell">
      <aside class="sidebar" id="sidebar" aria-label="Documentation">
        <div class="sidebar__brand">
          <a class="brand" href="/${SITE.homeSlug}">
            <img class="brand__logo" src="/logo_v.svg" width="37" height="30" alt="Valvur" />
            <span class="brand__text">
              <span class="brand__name">Valvur</span>
              <span class="brand__wiki">Wiki</span>
            </span>
          </a>
          <button class="sidebar__close" type="button" data-action="close-nav" aria-label="Close navigation">×</button>
        </div>
        <nav class="sidebar__nav" id="sidebar-nav"></nav>
        <div class="sidebar__footer">
          ${renderContactBlock('sidebar')}
          <div class="sidebar__footer-links">
            ${SITE.externalLinks
              .map(
                (link) =>
                  `<a href="${link.href}" target="_blank" rel="noopener noreferrer">${link.label}</a>`,
              )
              .join('')}
          </div>
        </div>
      </aside>

      <div class="main">
        <header class="topbar">
          <button class="topbar__menu" type="button" data-action="open-nav" aria-label="Open navigation">
            <span></span><span></span><span></span>
          </button>
          <div class="topbar__search">
            <label class="sr-only" for="wiki-search">Search docs</label>
            <input id="wiki-search" type="search" placeholder="Search docs…" autocomplete="off" />
          </div>
          <p class="topbar__tagline">${SITE.tagline}</p>
        </header>

        <div class="content-wrap">
          <article class="doc" id="doc-root"></article>
          <aside class="toc" id="toc" aria-label="On this page"></aside>
        </div>
      </div>

      <div class="backdrop" data-action="close-nav" hidden></div>
    </div>
  `;
}

export function renderSidebarNav(activeSlug: string): string {
  return NAV.map((section) => {
    const items = section.items
      .map((item) => renderNavLink(item, activeSlug))
      .join('');
    return `
      <div class="nav-section">
        <p class="nav-section__title">${section.title}</p>
        <ul class="nav-section__list">${items}</ul>
      </div>
    `;
  }).join('');
}

function renderNavLink(item: NavItem, activeSlug: string): string {
  const active = item.slug === activeSlug ? ' is-active' : '';
  const stub = item.stub ? ' is-stub' : '';
  const badge = item.stub ? '<span class="nav-badge">Soon</span>' : '';
  return `
    <li>
      <a class="nav-link${active}${stub}" href="/${item.slug}">
        <span>${item.title}</span>
        ${badge}
      </a>
    </li>
  `;
}

export function renderToc(
  headings: Array<{ id: string; text: string; level: number }>,
): string {
  if (headings.length < 2) {
    return '';
  }

  const links = headings
    .map(
      (heading) => `
      <li class="toc__item toc__item--h${heading.level}">
        <a href="#${heading.id}">${heading.text}</a>
      </li>
    `,
    )
    .join('');

  return `
    <p class="toc__title">On this page</p>
    <ul class="toc__list">${links}</ul>
  `;
}

export function renderNotFound(slug: string): string {
  return `
    <div class="doc-header">
      <p class="eyebrow">404</p>
      <h1>Page not found</h1>
      <p class="lede">No wiki page exists for <code>${slug}</code>.</p>
    </div>
    <p><a href="/${SITE.homeSlug}">Return to overview</a></p>
  `;
}

export function renderStubNotice(title: string): string {
  return `
    <div class="callout callout--info">
      <strong>Coming soon.</strong>
      This page is reserved in the information architecture for <em>${title}</em>.
      Phase 1 documents the overview, kid app, parent layout, and patrol layout first.
    </div>
  `;
}

export function renderPager(prev?: NavItem, next?: NavItem): string {
  if (!prev && !next) return '';

  return `
    <nav class="pager" aria-label="Page navigation">
      ${
        prev
          ? `<a class="pager__link pager__link--prev" href="/${prev.slug}">
              <span class="pager__label">Previous</span>
              <span class="pager__title">${prev.title}</span>
            </a>`
          : '<span></span>'
      }
      ${
        next
          ? `<a class="pager__link pager__link--next" href="/${next.slug}">
              <span class="pager__label">Next</span>
              <span class="pager__title">${next.title}</span>
            </a>`
          : '<span></span>'
      }
    </nav>
  `;
}
